import { useLingui } from '@lingui/react/macro';
import { cn } from '@worldcoo/ui/lib/utils';
import { diamondGlows, DiamondGrid } from '../primitives';

type ValueProp = { title: string; description: string; surface: string; tilt: string; lift: string };

/**
 * Angles measured off the hand-off, ~+4.5/-5 alternating with a ~70px drop on even cards: same-sign
 * angles read as a crooked page. Cards overlap 16px (centres ~332px apart, cards ~348px), and
 * `transform` makes DOM order the paint order, so no `z-index`.
 */
export function ValueProps() {
	const { t } = useLingui();

	const items: ValueProp[] = [
		{
			title: t`Connection`,
			description: t`Immediate access to agreements with more than 1,000 audited NGOs. You pick the cause that best matches your values.`,
			surface: 'bg-sand',
			tilt: 'rotate-[4.5deg]',
			lift: 'xl:translate-y-0',
		},
		{
			title: t`Pride of belonging`,
			description: t`Build real engagement among your employees and loyalty among your customers by tying your brand to measurable impact.`,
			surface: 'bg-sage',
			tilt: '-rotate-[5deg]',
			lift: 'xl:translate-y-[70px]',
		},
		{
			title: t`CSR innovation`,
			description: t`A digital and transparent channel that raises your sustainability strategy.`,
			surface: 'bg-apricot',
			tilt: 'rotate-[4deg]',
			lift: 'xl:translate-y-1',
		},
		{
			title: t`100% customizable`,
			description: t`Tools tailored to what each company needs.`,
			surface: 'bg-ink text-white',
			tilt: '-rotate-[5deg]',
			lift: 'xl:translate-y-[68px]',
		},
	];

	return (
		<section className="relative overflow-hidden bg-white">
			<DiamondGrid
				opacity={1}
				masks={[diamondGlows({ x: 'calc(100% + 105px)', y: '18%' }, { x: '-105px', y: 'calc(100% + 26px)' })]}
			/>

			<div className="relative mx-auto max-w-[1440px] px-6 py-16 md:pt-24 md:pb-32 xl:px-10 xl:pb-40">
				<h2 className="text-center text-3xl leading-[1.2] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Our value proposition`}
				</h2>

				{/* `px-2` on the stack, not the cards: equal widths, with room for tilted corners inside `overflow-hidden`. */}
				<div className="mt-10 flex flex-col -space-y-4 px-2 md:mt-20 md:grid md:grid-cols-2 md:gap-6 md:space-y-0 md:px-0 xl:flex xl:flex-row xl:gap-0">
					{items.map((item) => (
						<article
							key={item.title}
							className={cn(
								'flex flex-col gap-2 rounded-3xl px-8 py-10 md:py-16 xl:-ml-4 xl:flex-1 xl:first:ml-0',
								item.surface,
								item.tilt,
								item.lift,
							)}
						>
							<h3 className="text-2xl leading-[1.3] font-medium tracking-[-0.02em]">{item.title}</h3>
							<p
								className={cn(
									'text-sm leading-[1.5]',
									item.surface.includes('bg-ink') ? 'text-white/70' : 'text-ink/80',
								)}
							>
								{item.description}
							</p>
						</article>
					))}
				</div>
			</div>
		</section>
	);
}
